import { useState } from "react"
import { useNavigate } from "@tanstack/react-router"

import type { AgentRecommendation } from "@productlane/zero/schema"
import { mutators } from "@productlane/zero/mutators"
import { GUIDANCE_CHANGED_ERROR } from "@productlane/zero/mutators/agent-recommendations"

import type { SuggestionRow } from "../helpCenter/use-article-suggestions"
import type { DocsSaves } from "./docs-change"
import { toast } from "@/components/toast"
import { captureError } from "@/lib/capture-error"
import { generateId } from "@/lib/id"
import { mutateOk, useAuthData, useZero } from "@/lib/zero"
import { planBulkAccept } from "../helpCenter/use-bulk-review-actions"

export const DISMISS_REASONS = [
	"Not accurate",
	"Already covered",
	"Not worth fixing",
	"Something else",
] as const

// "Not worth documenting" is the reason the docs agent learns a topic rule from.
export const DOCS_DECLINE_REASONS = [
	"Not accurate",
	"Already covered",
	"Not worth documenting",
	"Something else",
] as const

export function useRecommendationActions({
	recommendation,
	docsGroup,
	docsRows,
	docsSaves,
	onClosed,
}: {
	recommendation: AgentRecommendation
	docsGroup?: DocsGroupState | null
	docsRows: SuggestionRow[]
	docsSaves?: DocsSaves
	onClosed: () => void
}) {
	const zero = useZero()
	const navigate = useNavigate()
	const authData = useAuthData()
	const [busy, setBusy] = useState(false)

	const report = (error: unknown, action: string) => {
		captureError(error, {
			domain: "general",
			action,
			workspaceId: authData.workspaceId,
			userId: authData.userId,
			extra: { recommendationId: recommendation.id },
		})
	}

	const accept = async (guidance?: string) => {
		if (busy) return
		setBusy(true)
		try {
			let newArticleId: string | null = null
			if (isDocsType(recommendation.type)) {
				if (docsSaves && !(await docsSaves.settle())) return
				const accepted = await acceptDocsGroup({
					zero,
					group: docsGroup,
					rows: docsRows,
				})
				newArticleId = accepted.newArticleId
			}
			await mutateOk(
				zero.mutate(
					mutators.agentRecommendations.accept({
						id: recommendation.id,
						guidance,
					}),
				),
			)
			toast.success({ title: acceptedTitle(recommendation.type) })
			if (newArticleId)
				void navigate({ to: "/edit-docs/$id", params: { id: newArticleId } })
			else onClosed()
		} catch (error) {
			if (error instanceof Error && error.message === GUIDANCE_CHANGED_ERROR) {
				toast.info({
					title: "Your guidance changed since this was suggested",
					description:
						"Edit the change, or open the guidance to apply it by hand.",
					action: {
						label: "Open guidance",
						callback: () => void navigate({ to: "/settings/ai/agent" }),
					},
				})
				return
			}
			report(error, "accept agent recommendation")
			toast.error({ title: "Couldn't apply this change" })
		} finally {
			setBusy(false)
		}
	}

	const dismiss = async (reason: string, note = "") => {
		if (busy) return
		setBusy(true)
		const rejected = docsRows.flatMap((row) => row.proposals).map((p) => p.id)
		try {
			if (isDocsType(recommendation.type) && rejected.length > 0) {
				// One group decline, so the docs agent learns from it once.
				await rejectDocsGroup({
					zero,
					proposalGroupId: recommendation.proposalGroupId ?? null,
					proposalIds: rejected,
					reason,
					note,
				})
			}
			await mutateOk(
				zero.mutate(
					mutators.agentRecommendations.dismiss({
						id: recommendation.id,
						reason,
					}),
				),
			)
			toast.success({
				title: "Dismissed",
				action: {
					label: "Undo",
					callback: () => {
						reopenProposals(zero, rejected, (error) =>
							report(error, "undo agent recommendation dismiss"),
						)
						void zero
							.mutate(
								mutators.agentRecommendations.reopen({ id: recommendation.id }),
							)
							.client.catch((error: unknown) =>
								report(error, "undo agent recommendation dismiss"),
							)
					},
				},
			})
			onClosed()
		} catch (error) {
			report(error, "dismiss agent recommendation")
			toast.error({ title: "Couldn't dismiss this suggestion" })
		} finally {
			setBusy(false)
		}
	}

	return { accept, dismiss, busy }
}

// A contradiction is fixed by editing the docs, just like a missing article.
export function isDocsType(type: AgentRecommendation["type"]): boolean {
	return type === "CONTENT" || type === "CONTRADICTION"
}

function acceptedTitle(type: AgentRecommendation["type"]): string {
	switch (type) {
		case "CONTENT":
		case "CONTRADICTION":
			return "Docs updated"
		case "GUIDANCE":
			return "Guidance updated"
		default:
			return "Marked as done"
	}
}

type Zero = ReturnType<typeof useZero>

export async function rejectDocsGroup({
	zero,
	proposalGroupId,
	proposalIds,
	reason,
	note,
}: {
	zero: Zero
	proposalGroupId: string | null
	proposalIds: readonly string[]
	reason: string
	note: string
}) {
	const learn = { reason, ...(note ? { note } : {}) }
	if (proposalGroupId) {
		await mutateOk(
			zero.mutate(
				mutators.helpCenterProposalGroups.rejectGroup({
					id: proposalGroupId,
					...learn,
				}),
			),
		)
		return
	}
	for (const id of proposalIds) {
		await mutateOk(
			zero.mutate(mutators.helpCenterArticleProposals.reject({ id, ...learn })),
		)
	}
}

// Reopening a child reopens its group too.
export function reopenProposals(
	zero: Zero,
	proposalIds: readonly string[],
	onError: (error: unknown) => void,
) {
	for (const id of proposalIds) {
		void zero
			.mutate(mutators.helpCenterArticleProposals.reopen({ id }))
			.client.catch(onError)
	}
}

export interface DocsGroupState {
	id: string
	status: string
	proposals?: readonly { id: string; status: string }[]
}

// One `acceptGroup` when it covers every open child: a single local write and
// one server transaction, where a mutation per proposal queued behind each other.
// Resolves to the first new article that has a folder, which the docs editor can open.
export async function acceptDocsGroup({
	zero,
	group,
	rows,
}: {
	zero: Zero
	group: DocsGroupState | null | undefined
	rows: readonly SuggestionRow[]
}): Promise<{ newArticleId: string | null }> {
	const proposals = rows.flatMap((row) => row.proposals)
	const steps = planBulkAccept({ proposals, newId: generateId })
	const accepts = steps.flatMap((step) =>
		step.action === "accept" ? [step.args] : [],
	)
	const planned = new Set(accepts.map((args) => args.proposalId))
	const openChildren = (group?.proposals ?? []).filter(
		(p) => p.status === "OPEN" || p.status === "DRAFT",
	)
	const coversGroup =
		group?.status === "OPEN" &&
		accepts.length === steps.length &&
		openChildren.length > 0 &&
		openChildren.every((p) => planned.has(p.id))

	if (group && coversGroup) {
		await mutateOk(
			zero.mutate(
				mutators.helpCenterProposalGroups.acceptGroup({
					id: group.id,
					childAcceptArgs: accepts.map((args) => ({
						proposalId: args.proposalId,
						newArticleVersionId: args.newArticleVersionId,
						newArticleId: args.newArticleId,
						newArticleUrlName: args.newArticleUrlName,
					})),
				}),
			),
		)
	} else {
		for (const step of steps) {
			await mutateOk(
				zero.mutate(
					step.action === "reject"
						? mutators.helpCenterArticleProposals.reject({
								id: step.proposalId,
							})
						: mutators.helpCenterArticleProposals.accept({
								id: step.args.proposalId,
								newArticleVersionId: step.args.newArticleVersionId,
								newArticleId: step.args.newArticleId,
								newArticleUrlName: step.args.newArticleUrlName,
							}),
				),
			)
		}
	}

	const created = accepts.find((args) => {
		const proposal = proposals.find((p) => p.id === args.proposalId)
		return proposal?.kind === "CREATE" && !!proposal.proposedGroupId
	})
	return { newArticleId: created?.newArticleId ?? null }
}
